import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import _ from 'lodash';
import { PodType } from '@scale/shared-types';
import {
  IOnboardingFlow,
  IOnboardingFlowDraft,
  OnboardingFlowNode,
  OnboardingFlowTransition,
  OnboardingFlowNodeType,
  OnboardingStepType,
  TransitionCondition,
  ConditionType,
  LoadBalancerCondition,
} from '@scale/shared/onboardingControlPanel';
import uuid from 'uuid';

type DraftFlowStore = {
  draftFlows: { [key: string]: IOnboardingFlowDraft<string> };
  addDraftFlow: (flow: IOnboardingFlowDraft<string>) => void;
  replaceDraftFlow: (oldId: string, flow: IOnboardingFlowDraft<string>) => void;
  removeDraftFlow: (flowId: string) => void;
  editFlowName: (
    flowId: string,
    newName: string,
  ) => IOnboardingFlow | IOnboardingFlowDraft<string> | null;
  editDraftEntityName: (
    flowId: string,
    oldName: string,
    newName: string,
  ) => OnboardingFlowNode | OnboardingFlowTransition | null;
  editDraftEntityValue: (
    flowId: string,
    entity: OnboardingFlowNode | OnboardingFlowTransition,
    value: any, // fix type
  ) => OnboardingFlowNode | OnboardingFlowTransition | null;
  deleteDraftEntity: (
    flowId: string,
    entity: OnboardingFlowNode | OnboardingFlowTransition | null,
  ) => void;
  addNode: (flowId: string, startingNode: OnboardingFlowNode) => void;
  addSourceNode: (flowId: string, startingNode: OnboardingFlowNode) => void;
  addTransition: (flowId: string, sourceNodeName: string, targetNodeName: string) => void;
  editDraftPodType: (flowId: string, podType: PodType) => void;
  editDraftContributorFacingName: (flowId: string, newName: string) => void;
  editDraftContributorFacingDescription: (flowId: string, newDescription: string) => void;
};

const numEdgesBetween = (edges: OnboardingFlowTransition[], sourceNode: string, destNode: string) =>
  edges.filter(edge => edge.sourceNode === sourceNode && edge.destNode === destNode).length;

export const useDraftFlowStore = create<DraftFlowStore, [['zustand/persist', DraftFlowStore]]>(
  persist(
    (set, get) => ({
      draftFlows: {},
      addDraftFlow: flow =>
        set({
          draftFlows: {
            ...get().draftFlows,
            [flow.originalFlowId || flow._id]: flow, // _id starts with tempId_ if it's a newly created flow
          },
        }),
      replaceDraftFlow: (oldId, draft) => {
        const draftFlows = get().draftFlows;
        delete draftFlows[oldId];
        draftFlows[draft.originalFlowId] = draft;
        set({ draftFlows });
      },
      removeDraftFlow: flowId => {
        const draftFlows = get().draftFlows;
        delete draftFlows[flowId];
        set({ draftFlows });
      },
      editFlowName: (id, newName) => {
        if (get().draftFlows[id]) {
          const draftFlows = get().draftFlows;
          const flow = _.cloneDeep(draftFlows[id]);
          flow.name = newName;
          draftFlows[id] = flow;
          set({ draftFlows });
          return flow;
        }
        return null;
      },
      editDraftEntityName: (flowId, oldName, newName) => {
        const flow = get().draftFlows[flowId];
        const nodeIndex = flow.nodes.findIndex((node: OnboardingFlowNode) => node.name === oldName);
        const edgeIndex = flow.edges.findIndex(
          (edge: OnboardingFlowTransition) => edge.name === oldName,
        );
        if (nodeIndex !== -1) {
          flow.nodes[nodeIndex].name = newName;
          flow.edges = flow.edges.map((edge: OnboardingFlowTransition) => {
            const newEdge = { ...edge };
            if (newEdge.sourceNode === oldName) newEdge.sourceNode = newName;
            if (newEdge.destNode === oldName) newEdge.destNode = newName;
            if (newEdge.name.includes(oldName))
              newEdge.name = newEdge.name.replace(oldName, newName);
            return newEdge;
          });
          set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
          return flow.nodes[nodeIndex];
        } else if (edgeIndex !== -1) {
          flow.edges[edgeIndex].name = newName;
          set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
          return flow.edges[edgeIndex];
        }
        return null;
      },
      editDraftEntityValue: (flowId, entity, value) => {
        const flow = get().draftFlows[flowId];
        if (!flow) {
          return null;
        }
        const nodeIndex = flow.nodes.findIndex(
          (node: OnboardingFlowNode) => node.name === (entity as OnboardingFlowNode)?.name,
        );
        const edgeIndex = flow.edges.findIndex(
          (edge: OnboardingFlowTransition) =>
            edge.name === (entity as OnboardingFlowTransition)?.name,
        );
        if (nodeIndex !== -1) {
          flow.nodes[nodeIndex] = value;
          set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
          return value;
        } else if (edgeIndex !== -1) {
          flow.edges[edgeIndex] = value;
          set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
          return value;
        }
        return null;
      },
      deleteDraftEntity: (flowId, entity) => {
        const flow = get().draftFlows[flowId];
        const nodeIndex = flow.nodes.findIndex(
          (node: OnboardingFlowNode) => node.name === (entity as OnboardingFlowNode)?.name,
        );
        const edgeIndex = flow.edges.findIndex(
          (edge: OnboardingFlowTransition) =>
            edge.name === (entity as OnboardingFlowTransition)?.name,
        );
        if (nodeIndex !== -1 && entity !== null) {
          flow.nodes.splice(nodeIndex, 1);
          flow.edges = flow.edges.filter(
            (edge: OnboardingFlowTransition) =>
              edge.sourceNode !== entity.name && edge.destNode !== entity.name,
          );
          set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
        } else if (edgeIndex !== -1 && entity !== null) {
          flow.edges.splice(edgeIndex, 1);
          set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
        }
      },
      addNode: (flowId: string, sourceNode: OnboardingFlowNode) => {
        const newNodeName = `New Node ${uuid.v4().substring(0, 4)}`;
        const flow = get().draftFlows[flowId];
        const newNode: OnboardingFlowNode = {
          name: newNodeName,
          type: OnboardingFlowNodeType.Step,
          stepType: OnboardingStepType.Dummy,
          entryHooks: [],
          exitHooks: [],
        };
        flow.nodes = [...flow.nodes, newNode];
        const conditions: TransitionCondition[] = [];
        if (
          sourceNode.type === OnboardingFlowNodeType.Step &&
          sourceNode.stepType === OnboardingStepType.LoadBalancer
        ) {
          const edgesFromSourceNode = flow.edges.filter(
            edge => edge.sourceNode === sourceNode.name,
          );
          if (edgesFromSourceNode.length === 0) {
            conditions.push({
              type: ConditionType.LoadBalancer,
              percentage: 100,
            });
          } else {
            const lbCondition = edgesFromSourceNode[0].conditions[0] as LoadBalancerCondition;
            if (!_.isUndefined(lbCondition.percentage)) {
              const totalPercentage = edgesFromSourceNode.reduce(
                (acc, edge) =>
                  acc + ((edge.conditions[0] as LoadBalancerCondition).percentage ?? 0),
                0,
              );
              const remainingPercentage = 100 - totalPercentage;
              conditions.push({
                type: ConditionType.LoadBalancer,
                percentage: Math.max(remainingPercentage, 0),
              });
            } else {
              conditions.push({
                type: ConditionType.LoadBalancer,
                capacity: 100,
              });
            }
          }
        }
        const numStartToNewNodeEdges = numEdgesBetween(flow.edges, sourceNode.name, newNodeName);
        let edgeName: string;
        if (numStartToNewNodeEdges === 0) {
          edgeName = `${sourceNode.name} -> ${newNodeName}`;
        } else {
          edgeName = `${sourceNode.name} -> ${newNodeName} (${numStartToNewNodeEdges})`;
        }
        flow.edges = [
          ...flow.edges,
          {
            name: edgeName,
            sourceNode: sourceNode.name,
            destNode: newNodeName,
            conditions,
          },
        ];
        set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
      },
      addSourceNode: (flowId: string, destinationNode: OnboardingFlowNode) => {
        const newNodeName = `New Node ${uuid.v4().substring(0, 4)}`;
        const flow = get().draftFlows[flowId];
        flow.nodes = [
          ...flow.nodes,
          {
            name: newNodeName,
            type: OnboardingFlowNodeType.Step,
            stepType: OnboardingStepType.Dummy,
            entryHooks: [],
            exitHooks: [],
          },
        ];

        flow.edges = [
          ...flow.edges,
          {
            name: `${newNodeName} -> ${destinationNode.name} (1)`,
            sourceNode: newNodeName,
            destNode: destinationNode.name,
            conditions: [],
          },
        ];
        set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
      },
      addTransition: (flowId: string, sourceNodeName: string, targetNodeName: string) => {
        const flow = get().draftFlows[flowId];

        const numEdgesSourceToTarget = numEdgesBetween(flow.edges, sourceNodeName, targetNodeName);
        let edgeName: string;
        if (numEdgesSourceToTarget === 0) {
          edgeName = `${sourceNodeName} -> ${targetNodeName}`;
        } else {
          edgeName = `${sourceNodeName} -> ${targetNodeName} (${numEdgesSourceToTarget})`;
        }
        flow.edges = [
          ...flow.edges,
          {
            name: edgeName,
            sourceNode: sourceNodeName,
            destNode: targetNodeName,
            conditions: [],
          },
        ];
        set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
      },
      editDraftPodType: (flowId: string, podType: PodType) => {
        const flow = get().draftFlows[flowId];
        flow.podType = podType;
        set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
      },
      editDraftContributorFacingName: (flowId: string, newName: string) => {
        const flow = get().draftFlows[flowId];
        flow.externalName = newName;
        set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
      },
      editDraftContributorFacingDescription: (flowId: string, newDescription: string) => {
        const flow = get().draftFlows[flowId];
        flow.externalDescription = newDescription;
        set({ draftFlows: { ...get().draftFlows, [flowId]: flow } });
      },
    }),
    {
      name: 'ocp-draft-flow-store',
    },
  ),
);
